import React, { createContext, useContext, useState, useCallback, useRef } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';

const TransitionContext = createContext();

export const usePageTransition = () => useContext(TransitionContext);

const CLOSE_DURATION = 0.45;
const OPEN_DURATION = 0.45;
const HOLD_MS = 150;

export const PageTransitionProvider = ({ children }) => {
  const navigate = useNavigate();
  const [phase, setPhase] = useState('idle');
  const lockRef = useRef(false);

  const transitionTo = useCallback((path) => {
    if (lockRef.current) return;
    lockRef.current = true;
    setPhase('closing');

    const closeMs = CLOSE_DURATION * 1000;
    const holdMs = HOLD_MS;
    const openMs = OPEN_DURATION * 1000;

    setTimeout(() => {
      navigate(path);
      window.scrollTo(0, 0);
      document.documentElement.scrollTop = 0;
    }, closeMs);

    setTimeout(() => {
      setPhase('opening');
    }, closeMs + holdMs);

    setTimeout(() => {
      setPhase('idle');
      lockRef.current = false;
    }, closeMs + holdMs + openMs + 50);
  }, [navigate]);

  return (
    <TransitionContext.Provider value={{ transitionTo, isTransitioning: phase !== 'idle' }}>
      {children}
      {phase !== 'idle' && <TransitionOverlay phase={phase} />}
    </TransitionContext.Provider>
  );
};

const easing = [0.76, 0, 0.24, 1];

const TransitionOverlay = ({ phase }) => {
  const isClosing = phase === 'closing';

  return (
    <div className="fixed inset-0" style={{ zIndex: 99999, pointerEvents: 'none' }}>
      {/* Top half */}
      <motion.div
        className="absolute top-0 left-0 right-0 h-[50vh]"
        style={{ background: '#0a0a0a' }}
        initial={{ y: '-101%' }}
        animate={{ y: isClosing ? '0%' : '-101%' }}
        transition={{ duration: isClosing ? CLOSE_DURATION : OPEN_DURATION, ease: easing }}
      >
        <motion.div
          className="absolute bottom-0 left-0 right-0 h-[2px]"
          style={{ background: 'linear-gradient(90deg, transparent 0%, #FFD700 30%, #FFD700 70%, transparent 100%)' }}
          initial={{ scaleX: 0 }}
          animate={{ scaleX: isClosing ? 1 : 0 }}
          transition={{ duration: isClosing ? CLOSE_DURATION * 0.8 : OPEN_DURATION * 0.5, ease: easing, delay: isClosing ? CLOSE_DURATION * 0.2 : 0 }}
        />
      </motion.div>

      {/* Bottom half */}
      <motion.div
        className="absolute bottom-0 left-0 right-0 h-[50vh]"
        style={{ background: '#0a0a0a' }}
        initial={{ y: '101%' }}
        animate={{ y: isClosing ? '0%' : '101%' }}
        transition={{ duration: isClosing ? CLOSE_DURATION : OPEN_DURATION, ease: easing }}
      >
        <motion.div
          className="absolute top-0 left-0 right-0 h-[2px]"
          style={{ background: 'linear-gradient(90deg, transparent 0%, #FFD700 30%, #FFD700 70%, transparent 100%)' }}
          initial={{ scaleX: 0 }}
          animate={{ scaleX: isClosing ? 1 : 0 }}
          transition={{ duration: isClosing ? CLOSE_DURATION * 0.8 : OPEN_DURATION * 0.5, ease: easing, delay: isClosing ? CLOSE_DURATION * 0.2 : 0 }}
        />
      </motion.div>

      {/* Center branding */}
      <motion.div
        className="absolute inset-0 flex items-center justify-center"
        initial={{ opacity: 0 }}
        animate={{ opacity: isClosing ? 1 : 0 }}
        transition={{ duration: 0.25, delay: isClosing ? CLOSE_DURATION * 0.6 : 0 }}
      >
        <span className="font-display text-2xl md:text-3xl tracking-[0.4em] text-[#FFD700]">
          DOUBLE A STUDIO
        </span>
      </motion.div>
    </div>
  );
};
